import React, { ComponentType, useEffect, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import CancelWarningModal from 'src/dispensary-admin/menu/modals/cancel-warning-modal';

type GuardLocation = {
  pathname: string;
  search: string;
};

export type RouteLeavingGuardModalProps = {
  isOpen: boolean;
  onClickNo: () => Promise<void>;
  onClickYes: () => Promise<void>;
  onClose: () => void;
};

type MaybePromise<T> = Promise<T> | T;

export type RouteLeavingGuardProps = {
  blockedQueryParams?: string[];
  handleConfirm?: () => MaybePromise<boolean | void>;
  handleDeny?: () => MaybePromise<void>;
  modal?: ComponentType<RouteLeavingGuardModalProps>;
  when: boolean;
};

function RouteLeavingGuard({
  when,
  // JSX component constructors must be PascalCase.
  // eslint-disable-next-line @typescript-eslint/naming-convention
  modal: Modal = CancelWarningModal,
  handleConfirm = () => undefined,
  handleDeny = () => undefined,
  blockedQueryParams = [],
}: RouteLeavingGuardProps): JSX.Element {
  const [modalVisible, setModalVisible] = useState(false);
  const [nextLocation, setNextLocation] = useState<GuardLocation | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const history = useHistory();

  // when no is clicked, call the handleDeny func
  // then navigate to next location
  const handleDenyClick = async (): Promise<void> => {
    await handleDeny();
    setModalVisible(false);
    setConfirmedNavigation(true);
  };

  // when yes is clicked, close this prompt and call the handleConfirm func, then navigate to the
  // next location — unless handleConfirm reports the user should stay: it resolves to exactly
  // `false` (they backed out of a nested confirmation, e.g. a stranding warning) or it throws (the
  // save failed, so keep them on the page with their unsaved changes rather than navigating away).
  // The prompt is closed first so a nested confirmation raised by handleConfirm is not stacked on
  // top of it.
  const handleConfirmClick = async (): Promise<void> => {
    setModalVisible(false);
    let result;
    try {
      result = await handleConfirm();
    } catch (error) {
      console.error(error);
      return;
    }
    if (result === false) {
      return;
    }
    setConfirmedNavigation(true);
  };

  // when close is clicked, just close the modal
  // and don't navigate anywhere
  const handleClose = (): void => {
    setModalVisible(false);
  };

  // when this function returns false, navigation is blocked completely
  const handleBlockedNavigation = (nextLocationFromPrompt: GuardLocation): boolean => {
    if (confirmedNavigation) {
      return true;
    }

    const isSamePathname = nextLocationFromPrompt.pathname === history.location.pathname;

    // A change to a caller-designated "blocked" query param (e.g. `category`)
    // switches which record is being edited, discarding the in-progress
    // changes the same way a real navigation would, so it must still prompt.
    const currentSearchParams = new URLSearchParams(history.location.search);
    const nextSearchParams = new URLSearchParams(nextLocationFromPrompt.search);
    const changesBlockedParam = blockedQueryParams.some(
      (param) => currentSearchParams.get(param) !== nextSearchParams.get(param)
    );

    // Query param only updates (e.g. filter/search/sort changes) on the same
    // page replace the URL via history but aren't actually navigation away
    // from the page, so they shouldn't trigger the unsaved changes prompt.
    if (isSamePathname && !changesBlockedParam) {
      return true;
    }

    setModalVisible(true);
    setNextLocation(nextLocationFromPrompt);
    return false;
  };

  useEffect(() => {
    if (confirmedNavigation && nextLocation) {
      // Navigate to the previous blocked location
      history.push(nextLocation.pathname + nextLocation.search);
      setConfirmedNavigation(false);
    }
  }, [confirmedNavigation, nextLocation, history]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />
      {modalVisible && (
        <Modal isOpen onClickYes={handleConfirmClick} onClickNo={handleDenyClick} onClose={handleClose} />
      )}
    </>
  );
}

export default RouteLeavingGuard;

// import RouteLeavingGuard from 'src/routes/route-leaving-guard';
// <RouteLeavingGuard
//   when={hasChanged}
//   modal={CancelWarningModal}
//   handleConfirm={this.handleModalClickYes}
//   handleDeny={this.handleModalClickNo}
// />
